/**
 * Runtime white-label branding for the caller's tenant.
 *
 * Reads /v1/branding/current — a customer inherits the parent
 * reseller's logo + primary color; a reseller / platform reads their
 * own. Empty fields are normal (the frontend falls back to the default
 * Volt tokens + EzSender wordmark in that case).
 *
 * Distinct from useAdminBranding (which writes to the reseller's own
 * row): this hook is for paint-time consumption everywhere — App
 * shell + sidebar — so the customer portal picks up the reseller's
 * branding without needing any admin scope.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface TenantBranding {
  logo_url?: string;
  primary_color?: string;
  custom_domain?: string;
  domain_verified: boolean;
  email_from_name?: string;
  email_reply_to?: string;
}

const BRANDING_KEY = ['branding', 'current'] as const;

export function useTenantBranding({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: BRANDING_KEY,
    queryFn: () => api.get<TenantBranding>('/v1/branding/current'),
    enabled,
    // Branding rarely changes; cache aggressively. The /admin/branding
    // save flow doesn't invalidate this key (operator's own admin GET
    // uses ['admin', 'branding']) — for now a manual refresh is fine
    // since the operator IS the one editing. If we later need live
    // re-paint after save, add invalidate to useUpdateBranding.
    staleTime: 5 * 60_000,
  });
}

/**
 * useApplyBranding — side-effecting variant. Renders nothing; injects
 * the reseller's primary color into the live :root CSS vars on mount
 * and re-applies on every change. Cleans up on unmount so an
 * impersonation tab swap (or logout) reverts to defaults.
 *
 * Mounted in AppShell so every authenticated route inherits the
 * effect. The sidebar's logo swap reads the same query separately.
 */
export function useApplyBranding(): void {
  const { data } = useTenantBranding();

  useEffect(() => {
    const color = (data?.primary_color ?? '').trim();
    const root = document.documentElement;
    // Empty / missing color → strip overrides so default tokens win.
    if (!color || !/^#[0-9a-fA-F]{6}$/.test(color)) {
      root.style.removeProperty('--volt');
      root.style.removeProperty('--volt-400');
      root.style.removeProperty('--volt-500');
      root.style.removeProperty('--volt-glow');
      return;
    }
    // Override the four high-traffic volt tokens. Other shades (50,
    // 100, 200, 300, 600, 700) keep their default green tints — for
    // MVP we accept the slight inconsistency on niche surfaces (top-up
    // pill backgrounds, etc.) rather than ship a derived-shade
    // generator. If polish is needed later, compute lighter/darker
    // shades from the picked color via a chroma library.
    root.style.setProperty('--volt', color);
    root.style.setProperty('--volt-400', color);
    root.style.setProperty('--volt-500', color);
    root.style.setProperty('--volt-glow', hexToRgba(color, 0.18));

    return () => {
      root.style.removeProperty('--volt');
      root.style.removeProperty('--volt-400');
      root.style.removeProperty('--volt-500');
      root.style.removeProperty('--volt-glow');
    };
  }, [data?.primary_color]);
}

// hexToRgba converts "#RRGGBB" to "rgba(r, g, b, a)". Caller validated
// the hex pattern; we trust the input.
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
